<template>
  <v-dialog
    :value="value"
    persistent
    scrollable
  >
    <v-card light>
      <v-card-title v-text="__('refund')" />
      <v-card-text>
        <v-list dense>
          <v-divider />
          <v-list-item-group
            v-model="selected"
            color="primary"
          >
            <template v-for="invoice in invoices">
              <v-list-item
                :key="`i${invoice.id}`"
                :disabled="! invoice.canBeRefunded"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ __('invoice') }} #{{ invoice.number }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-action-text>{{ invoice.total | currency }}</v-list-item-action-text>
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="`d${invoice.id}`" />
            </template>
          </v-list-item-group>
        </v-list>
      </v-card-text>
      <v-card-actions v-text="__('refundConfirm')" class="text-body-1" />
      <v-card-actions>
        <v-btn
          text
          color="grey darken-2"
          :disabled="refunding"
          @click="cancel()"
        >
          {{ __('cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          text
          :loading="refunding"
          :disabled="selected === undefined"
          @click="refundAndPrint()"
        >
          {{ __('ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapState, mapMutations } from 'vuex'
  import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'
  import PaymentMethod from '@/Models/Static/PaymentMethod'
  import Currenciable from '@/Mixins/Currenciable'
  import I18n from '@/Mixins/I18n'
  import PrintsTickets from '@/Mixins/PrintsTickets'
  import RequiresIntegration from '@/Mixins/RequiresIntegration'

  export default {
    mixins: [
      Currenciable,
      I18n,
      PrintsTickets,
      RequiresIntegration,
    ],

    props: {
      value: {
        required: true,
        type: Boolean,
      },
      order: {
        required: true,
        type: Object,
      },
    },

    data: () => ({
      FranceLog,
      PaymentMethod,
      invoices: [],
      selected: undefined,
    }),

    computed: {
      refunding: {
        get () {
          return this.$store.state.history.refunder.refunding
        },
        set (v) {
          this.$store.commit('history/refunder/refunding', v)
        }
      },
      ...mapState('history/refunder', [
        'refund',
      ]),
    },

    created () {
      this.invoices = this.order.orderInvoices
      if (this.invoices.length === 1) {
        this.selected = 0
      }
    },

    methods: {
      ...mapMutations('user', [
        'skipScreenLock',
      ]),
      cancel () {
        this.selected = undefined
        this.$emit('input', false)
      },
      async refundAndPrint () {
        this.refunding = true
        let refundCompleted = false
        try {
          await this._refund()
          refundCompleted = true
          await this._print()
          this.$store.dispatch('history/close')
        } catch (error) {
          // Only cancel refund if it failed BEFORE completing (before closeRefund)
          if (this.refund !== null && !refundCompleted) {
            await this.$store.dispatch('history/refunder/cancelRefund')
            error.response
              ? this.$store.dispatch('xError', error)
              : this.$store.dispatch('alert/showError', `${this.__('refundError')}: ${error.message}`)
          } else if (refundCompleted) {
            // Refund completed successfully but printing failed
            // Close history anyway and show warning about printing
            this.$store.dispatch('history/close')
            this.$store.dispatch('alert/showWarning', error.message || this.__('printError'))
          } else {
            // Unexpected error
            error.response
              ? this.$store.dispatch('xError', error)
              : this.$store.dispatch('alert/showError', `${this.__('refundError')}: ${error.message}`)
          }
        }
        this.refunding = false
      },
      async _refund () {
        // Extract card payments from original invoice BEFORE creating refund
        const originalInvoice = this.invoices[this.selected]
        const cardPayments = originalInvoice.orderPayments
          .filter(payment => this.PaymentMethod.isCard(payment.paymentMethod))

        // 1. FIRST: Process card refunds
        const voidReasons = []
        try {
          if (cardPayments.length > 0) {
            for (let index = 0; index < cardPayments.length; index++) {
              const voidReason = await this._refundCardPayment(cardPayments[index], index)
              voidReasons.push(voidReason)
            }
          }
        } catch (error) {
          this._printCardRefundErrors(error, cardPayments)
          throw error  // If it fails here, nothing has been created in XEF yet ✅
        }

        // 2. ONLY IF REFUNDS ARE OK: Create invoice (increment counter)
        await this.$store.dispatch('history/refunder/createRefund', {
          order: this.order,
          invoice: originalInvoice,
          voidReasons,
        })

        // 3. Continue with sign and close
        await this.$store.dispatch('history/refunder/signRefund')
        await this.$store.dispatch('history/refunder/closeRefund')
      },
      _refundCardPayment (payment, index) {
        this.skipScreenLock(true)
        return Bridge.interfaces.AndroidPayment.refund(
          payment.voidReason,
          Number(payment.payAmount * 100).toFixed(),
          JSON.stringify({
            test: !! this.integration?.test,
            config: this.integration?.config ?? {},
          })
        )
          .then(voidReason => {
            this.skipScreenLock(false)
            this.$log(`Refunded card payment (${payment.payAmount.toFixed(2)}): ${voidReason}`)
            return voidReason  // Return voidReason instead of updating state
          })
          .catch(error => {
            this.skipScreenLock(false)
            const message = error?.nativeMessage || 'Payment Interface does not exist'
            this.$log(`Refunding card payment error (${payment.payAmount.toFixed(2)}): ${message}`)
            error.failedCardPaymentIndex = index  // Add index for error handling
            throw error
          })
          // the finally method does not work in the current Bridge version
      },
      async _print () {
        try {
          await this.printTicket(this.refund, this.refund.orderInvoices[0])
        } catch (error) {
          this.$log(`Print error: ${error.message}`, 'Refund', this.refund.id)
          this.$logFrance(this.FranceLog.PRINTER_NOT_AVAILABLE, `Refund ${this.refund.id}`)
          throw new Error(this.__('printError'))
        }
      },
      _printCardRefundErrors (error, cardPayments) {
        let ok = true
        const cardRefunds = cardPayments.map((payment, index) => {
          let status = ok ? 'OK' : '-'
          if (error.failedCardPaymentIndex === index) {
            status = 'ERROR'
            ok = false
          }
          let identifier = this.formatPrice(payment.payAmount)
          // We don't show voidReason here because we don't have all of them yet
          return { status, identifier }
        })

        this.initPrinter()
        AndroidPrinter.separator()
        cardRefunds.forEach(cardRefund => {
          AndroidPrinter.text2(cardRefund.identifier, cardRefund.status)
        })
        AndroidPrinter.separator()
        AndroidPrinter.text(error.message, JSON.stringify({ align: 'left' }))
        AndroidPrinter.separator()

        Bridge.interfaces.AndroidPrinter.print()
      },
    },
  }
</script>
